// A Saju book page's printed text — its titles, captions, scores and manuscripts — was written for one person: the sample
// the book was built for (specs/011). The import records on each page whom and for which year (the page's
// `edusenseBookText`), and its manuscripts and figures are narrative components that know when their text no longer fits
// the person (component-saju-narrative.mjs). What they do not draw — the page's pictures (score arrows, month icons, the
// "현재" plaque) and which years and 대운 its pages hold — stays the sample's until pictures are components too (specs/011
// P7). So a page is kept from the PDF, print and publishing when:
//   · its pictures and page structure are the sample's: the document's person is another, or its reference year is another
//     and the page prints that year in its own words or figures that moved with it (a month, the change into the year);
//   · its manuscripts are to be written again, or its text overflows its lines, has a figure the chart cannot give or a
//     character the book's fonts cannot print — or the print metrics to tell are not loaded yet.
// A manuscript an agent wrote and no one has reviewed yet (a draft) does not keep a page out; the outputs name it.
import { movedFigures, narrativeIssues } from './component-saju-narrative.mjs';
import { printMetrics } from './print-wrap.mjs';

/** The birth input a book's text depends on (the reference year is recorded beside it). */
export const BOOK_TEXT_BIRTH = Object.freeze(['birthDate', 'birthTime', 'unknownTime', 'gender', 'isLunar', 'yajasi']);

const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const person = props => BOOK_TEXT_BIRTH.map(key => key === 'yajasi' ? props?.yajasi === true : props?.[key]);
const samePerson = (left, right) => JSON.stringify(person(left)) === JSON.stringify(person(right));

/** The person (and year) a page's printed text was written for ({ product, subject, year?, yearBound? }), or null. */
export function bookTextOf(section) {
  const value = section?.snapshot?.blocks?.props?.edusenseBookText;
  return object(value) && typeof value.product === 'string' && object(value.subject) ? value : null;
}

/**
 * The book text a page records: its `product`, the sample `subject` it was written for (birth input) and the year the book
 * reads as now; `yearBound` when the page's own words print that year (saju-narrative-import.mjs pageYearBound).
 */
export function bookText(product, subject, yearBound = false) {
  return { product, subject: Object.fromEntries(BOOK_TEXT_BIRTH.map(key => [key, key === 'yajasi' ? subject.yajasi === true : subject[key]])),
    ...(Number.isInteger(subject.year) ? { year: subject.year } : {}), ...(yearBound ? { yearBound: true } : {}) };
}

// Why a page's pictures and page structure are not the document's person's, and what keeps its text from printing.
export const SAMPLE_REASONS = Object.freeze({ person: '표본 인물의 그림·쪽 구성', year: '다른 기준 연도의 문구', figures: '바뀐 계산값과 맞지 않는 그림' });
export const TEXT_ISSUES = Object.freeze({ stale: '다시 써야 할 원고', overflow: '줄을 넘친 글', 'missing-figure': '계산할 수 없는 값',
  unsupported: '인쇄 글꼴에 없는 글자', unmeasured: '아직 재지 못한 줄바꿈' });

/**
 * Where each Saju book page stands for the outputs: [{ index, title, sample: [reason], text: { issue: count }, drafts }] for
 * the pages with anything to tell — `sample` and `text` keep a page out (bookPageBlocked), `drafts` counts the manuscripts
 * written but not reviewed. A Saju component of the document (a chart or a narrative) tells whom and which year it is for.
 */
export function bookPageStates(content) {
  const sections = content?.sections ?? [];
  const people = sections.flatMap(section => (section.components ?? [])
    .filter(component => typeof component?.type === 'string' && component.type.startsWith('saju.') && object(component.props) && typeof component.props.birthDate === 'string')
    .map(component => component.props));
  const metrics = printMetrics();
  return sections.flatMap((section, index) => {
    const printed = bookTextOf(section);
    const narratives = (section.components ?? []).filter(component => component?.type === 'saju.narrative' && object(component.props) && object(component.snapshot?.data));
    const sample = [], text = {};
    if (printed && people.some(props => !samePerson(props, printed.subject))) sample.push('person');
    else if (printed && Number.isInteger(printed.year) && people.some(props => props.year !== printed.year)) {
      if (printed.yearBound) sample.push('year');
      if (narratives.some(component => movedFigures(component.props, component.snapshot).length)) sample.push('figures');
    }
    for (const component of narratives) {
      for (const issue of narrativeIssues(component.props, component.snapshot)) text[issue.kind] = (text[issue.kind] ?? 0) + 1;
    }
    if (narratives.length && !metrics) text.unmeasured = 1;
    const drafts = narratives.filter(component => component.props.text?.review?.state === 'draft').length;
    return sample.length || Object.keys(text).length || drafts ? [{ index, title: section.title, sample, text, drafts }] : [];
  });
}

/** Whether a page's state keeps it out of the PDF, print and publishing. */
export const bookPageBlocked = state => state.sample.length > 0 || Object.keys(state.text).length > 0;

/** The pages kept out of the outputs: [{ index, title, sample, text, drafts }]. */
export const blockedBookPages = content => bookPageStates(content).filter(bookPageBlocked);

const listed = numbers => numbers.length > 12 ? `${numbers.slice(0, 12).join('·')}쪽 외 ${numbers.length - 12}쪽` : `${numbers.join('·')}쪽`;

/** Why `action` (a verb ready for '수 없습니다': 'PDF로 만들', '게시할') is refused for these pages, by reason and page number. */
export function blockedBookMessage(pages, action) {
  const numbers = reason => pages.filter(reason).map(page => page.index + 1);
  const parts = [
    ...Object.entries(SAMPLE_REASONS).filter(([reason]) => pages.some(page => page.sample.includes(reason)))
      .map(([reason, label]) => `${label} ${listed(numbers(page => page.sample.includes(reason)))}`),
    ...Object.entries(TEXT_ISSUES).filter(([issue]) => pages.some(page => page.text[issue]))
      .map(([issue, label]) => `${label} ${pages.reduce((sum, page) => sum + (page.text[issue] ?? 0), 0)}곳(${listed(numbers(page => page.text[issue]))})`),
  ];
  const sample = pages.some(page => page.sample.length);
  return `사주 책의 ${listed(pages.map(page => page.index + 1))}을 ${action} 수 없습니다 — ${parts.join(' · ')}. `
    + (sample ? '표본 명식과 기준 연도로 되돌리거나 책을 다시 가져오세요. 새 명식의 원고는 다시 쓸 수 있지만, 그림과 쪽 구성은 아직 표본의 것입니다.'
      : '원고를 다시 쓰거나 글을 줄여 주세요.');
}

// How the page list notes a state, shortly.
const SAMPLE_NOTES = Object.freeze({ person: '그림은 표본 인물', year: '다른 해의 문구', figures: '바뀐 값과 다른 그림' });
const TEXT_NOTES = Object.freeze({ stale: '다시 쓸 원고', overflow: '넘친 글', 'missing-figure': '계산 불가', unsupported: '없는 글자', unmeasured: '줄바꿈 확인 전' });
/** A page's state as the page list notes it: which kind comes first ('sample', 'text' or 'draft') and a short note. */
export function bookPageNote(state) {
  const notes = [...state.sample.map(reason => SAMPLE_NOTES[reason]),
    ...Object.entries(state.text).map(([issue, count]) => issue === 'unmeasured' ? TEXT_NOTES[issue] : `${TEXT_NOTES[issue]} ${count}`),
    ...(state.drafts ? [`검토 전 ${state.drafts}`] : [])];
  return { kind: state.sample.length ? 'sample' : Object.keys(state.text).length ? 'text' : 'draft', note: notes.join(' · ') };
}

/** The manuscripts written but not reviewed yet, for an output to name ('' when none). */
export function draftBookMessage(content) {
  const pages = bookPageStates(content).filter(state => state.drafts);
  return pages.length ? `검토 전 원고가 ${pages.reduce((sum, page) => sum + page.drafts, 0)}곳 있습니다(${listed(pages.map(page => page.index + 1))}).` : '';
}

/** Throws when `content` holds book pages kept out of the outputs. */
export function assertBookTextCurrent(content, action) {
  const pages = blockedBookPages(content);
  if (pages.length) throw Object.assign(new Error(blockedBookMessage(pages, action)), { code: 'BOOK_TEXT_STALE', pages: pages.map(page => page.index + 1) });
}
